// Audit log for platform administrators (FR-70): events of the app or vault
// database, newest first. "Load older" asks for the events before the last one
// shown. Metadata never holds personal data, so it is shown as recorded.
import {
  Button,
  Code,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Source = "app" | "vault";
type Filters = { action: string; actor: string; target: string };
type AuditEvent = Schemas["AuditEvent"];

export const AUDIT_PAGE = 50;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY: Filters = { action: "", actor: "", target: "" };

export function AdminAuditPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_audit.intro")}>
      <Audit />
    </AdminLayout>
  );
}

function Audit() {
  const { t } = useTranslation();
  const [source, setSource] = useState<Source>("app");
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const events = useInfiniteQuery({
    queryKey: ["admin", "audit", source, filters],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/admin/audit-events", {
          params: {
            query: {
              source,
              limit: AUDIT_PAGE,
              ...(filters.action ? { action: filters.action } : {}),
              ...(filters.actor ? { actor_id: filters.actor } : {}),
              ...(filters.target ? { target_id: filters.target } : {}),
              ...(pageParam ? { before: pageParam } : {}),
            },
          },
        }),
      ),
    // A full page may have more behind it; a short one is the end.
    getNextPageParam: (last) =>
      last.items.length === AUDIT_PAGE ? last.items[last.items.length - 1]?.created_at : undefined,
  });
  const items = events.data?.pages.flatMap((p) => p.items) ?? [];

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const next = { action: draft.action.trim(), actor: draft.actor.trim(), target: draft.target.trim() };
    if (next.actor && !UUID.test(next.actor)) return setError(t("admin_audit.bad_actor"));
    setError(null);
    setFilters(next);
  };
  const set = (k: keyof Filters) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft({ ...draft, [k]: e.currentTarget.value });

  return (
    <Stack gap="md">
      <Group gap="sm">
        <Text size="sm" id="audit-source">
          {t("admin_audit.source")}
        </Text>
        <SegmentedControl
          size="xs"
          aria-labelledby="audit-source"
          value={source}
          onChange={(v) => setSource(v as Source)}
          data={[
            { value: "app", label: t("admin_audit.source_app") },
            { value: "vault", label: t("admin_audit.source_vault") },
          ]}
        />
      </Group>
      <Paper withBorder p="md" radius="md">
        <form onSubmit={apply} data-testid="audit-filters">
          <Stack gap="sm">
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
              <TextInput
                label={t("admin_audit.action")}
                value={draft.action}
                onChange={set("action")}
                maxLength={80}
                spellCheck={false}
              />
              <TextInput
                label={t("admin_audit.actor")}
                value={draft.actor}
                onChange={set("actor")}
                spellCheck={false}
                autoComplete="off"
              />
              <TextInput
                label={t("admin_audit.target")}
                value={draft.target}
                onChange={set("target")}
                maxLength={80}
                spellCheck={false}
                autoComplete="off"
              />
            </SimpleGrid>
            <FormError message={error} />
            <Group gap="xs">
              <Button type="submit" size="xs">
                {t("admin_audit.apply")}
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={() => {
                  setDraft(EMPTY);
                  setFilters(EMPTY);
                  setError(null);
                }}
              >
                {t("admin_audit.reset")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
      {events.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : events.isError && items.length === 0 ? (
        <FormError message={errorMessage(events.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="audit-empty">
          {t("admin_audit.empty")}
        </Text>
      ) : (
        <>
          <AuditTable items={items} />
          {events.isFetchNextPageError && <FormError message={errorMessage(events.error, t("error.generic"))} />}
          {events.hasNextPage ? (
            <Button
              variant="default"
              w="fit-content"
              loading={events.isFetchingNextPage}
              onClick={() => void events.fetchNextPage()}
            >
              {t("admin_audit.older")}
            </Button>
          ) : (
            <Text size="sm" c="dimmed" data-testid="audit-end">
              {t("admin_audit.end")}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

function AuditTable({ items }: { items: AuditEvent[] }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <Table.ScrollContainer minWidth={760}>
      <Table striped verticalSpacing="sm" aria-label={t("admin.tab_audit")}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("admin_audit.time")}</Table.Th>
            <Table.Th>{t("admin_audit.event")}</Table.Th>
            <Table.Th>{t("admin_audit.actor_col")}</Table.Th>
            <Table.Th>{t("admin_audit.target_col")}</Table.Th>
            <Table.Th>{t("admin_audit.metadata")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((e) => (
            <Table.Tr key={e.id} data-testid="audit-row">
              <Table.Td>
                <Text size="sm">{f.dateTime(e.created_at)}</Text>
                <Text size="xs" c="dimmed" ff="monospace" title={t("admin_audit.request")}>
                  {e.request_id}
                </Text>
              </Table.Td>
              <Table.Td>
                <Code>{e.action}</Code>
              </Table.Td>
              <Table.Td>
                <Text size="xs" ff="monospace">
                  {e.actor_id ?? t("admin_audit.system")}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs">{e.target_type}</Text>
                <Text size="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                  {e.target_id}
                </Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  {Object.entries(e.metadata)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([k, v]) => (
                      <Text key={k} size="xs" style={{ wordBreak: "break-all" }}>
                        <Text span size="xs" fw={600}>
                          {k}
                        </Text>
                        {": "}
                        <Text span size="xs" ff="monospace">
                          {v}
                        </Text>
                      </Text>
                    ))}
                </Stack>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
